import React, { useState, useRef, useEffect } from 'react';
import { 
  Globe,
  Eye, 
  EyeOff,
  ArrowRight,
  LockKeyhole,
  ShieldCheck,
   Smartphone,
    ArrowLeft,
  Mail,
  Building2
} from 'lucide-react';
import styles from './ForgotPassword.module.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../../Components/Api/Api';
import Swal from 'sweetalert2';
import { BRANDING } from '../../Components/Branding/Branding';
import { GoShieldCheck } from 'react-icons/go';
import { RiPieChart2Fill } from 'react-icons/ri';
import { HiMiniUsers } from 'react-icons/hi2';
import { FaRegUser } from 'react-icons/fa';
import { LuLockKeyhole } from 'react-icons/lu';

export default function ForgotPassword() {
  // Form States
  const [identifier, setIdentifier] = useState(''); // Handles Membership Number or Email
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCompanySelection, setShowCompanySelection] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [emailError, setEmailError] = useState(''); // Direct red error text under input
  const [companies, setCompanies] = useState([]); // Stores fetched companies
  const [companiesLoading, setCompaniesLoading] = useState(false);
   // Mid-screen loader trigger
   const [email, setEmail] = useState('');
   const [step, setStep] = useState(1); 
   const otpRefs = useRef([]);
   
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(165); 
  
  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [language, setLanguage] = useState('English');
   const [showPassword1, setShowPassword1] = useState(false);
   const [password1, setPassword1] = useState('');
  
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  // Helper to fetch companies only after a successful login trigger
  const fetchCompanies = async (emailToFetch) => {
    setCompaniesLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/my-active-coop?email=${emailToFetch}`);
      const companyList = response.data?.companies || response.data?.data || [];
      
      setCompanies(companyList);

      if (companyList.length > 0) {
        setEmailError('');
        setSelectedCompanyId(companyList[0]?.id || '');
        return true; // Signifies successful retrieval
      } else {
        setSelectedCompanyId('');
        setEmailError(response.data?.message || 'No company found.');
        return false;
      }
    } catch (error) {
      let errorMessage = 'An error occurred fetching companies. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
        if (typeof message === 'string') errorMessage = message;
        else if (Array.isArray(message)) errorMessage = message.join('; ');
        else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          errorMessage = Array.isArray(messages) ? messages.join('; ') : messages;
        }
      }
      setEmailError(errorMessage);
      setCompanies([]);
      setSelectedCompanyId('');
      return false;
    } finally {
      setCompaniesLoading(false);
    }
  };

  // NOTE: existing login handler kept as-is but is no longer wired up to the form.
  // The form now uses handleContinue -> handleForgotPassword instead.
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/member-auth/login`, {
        email: identifier, 
        password: password,
      });
      
      const apiData = response.data?.data || {};
      setLoginData(apiData);
      
      // Save general user details first
      localStorage.setItem('token', apiData?.token);
      localStorage.setItem('memberNumber', apiData?.member?.member_number);
      localStorage.setItem('memberPhoneNumber', apiData?.member?.phone_number);
      localStorage.setItem('department', apiData?.member?.department);
      localStorage.setItem('employerName', apiData?.member?.employer_name);
      localStorage.setItem('saveType', apiData?.member?.savings_type);
      localStorage.setItem('memberName', apiData?.member?.name);
      localStorage.setItem('memberAddress', apiData?.member?.address);
      localStorage.setItem('memberPhoto', apiData?.member?.passport_photograph);
      localStorage.setItem('walletBalance', apiData?.member?.wallet_balance);
      
      // Fetch active cooperatives on successful login verification
      const companiesFetched = await fetchCompanies(identifier);

      if (companiesFetched) {
        // Show company selection layout
        setShowCompanySelection(true);
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Account Restricted',
          text: 'Login successful, but we could not find any active companies registered to this account.',
        });
      }
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
        if (typeof message === 'string') errorMessage = message;
        else if (Array.isArray(message)) errorMessage = message.join('; ');
        else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          errorMessage = Array.isArray(messages) ? messages.join('; ') : messages;
        }
      }
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // NOTE: existing handler kept as-is but not currently wired to any button.
  const handleCompanySubmit = (e) => {
    e.preventDefault();
    if (!selectedCompanyId) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a company to proceed.',
      });
      return;
    }

    const activeCompanies = companies.length > 0 ? companies : (loginData?.companies || loginData?.member?.companies || []);
    const selectedCompany = activeCompanies.find(c => String(c.id) === String(selectedCompanyId));

    if (selectedCompany) {
      localStorage.setItem('companyId', selectedCompany.id);
      localStorage.setItem('companyName', selectedCompany.name || selectedCompany.company_name);
      navigate('/dashboard'); 
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Invalid company selection.',
      });
    }
  };

  // Fired on every keystroke of the email field.
  // Resets the company selection step as soon as the user edits the email again.
  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    setIdentifier(value);
    setEmailError('');
    if (showCompanySelection) {
      setShowCompanySelection(false);
      setCompanies([]);
      setSelectedCompanyId('');
    }
  };

  // Fired when the user clicks "Continue" on the email-only screen.
  // Fetches the companies tied to the entered email, then reveals the
  // company selection field + swaps the Continue button for the Proceed button.
  const handleContinue = async () => {
    if (!identifier) {
      setEmailError('Please enter your email address.');
      return;
    }
    setLoading(true);
    const companiesFetched = await fetchCompanies(identifier);
    setLoading(false);

    if (companiesFetched) {
      setEmail(identifier);
      setShowCompanySelection(true);
    }
  };

    const handleOtpChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

    const handle2ndStep = async () => {
  setLoading(true);

  try {
    await axios.post(
      `${BASE_URL}/member-auth/member_verify_forgetOtp`,
      {
        email: email,
        otp: otp.join(""),
      }
    );

    // Call verify_forgetOtp after the first verification succeeds
    await handleVerifyOtp();

  } catch (error) {
    let errorMessage = "An error occurred. Please try again.";

    if (error.response?.data?.message) {
      const message = error.response.data.message;

      if (typeof message === "string") {
        errorMessage = message;
      } else if (Array.isArray(message)) {
        errorMessage = message.join("; ");
      } else if (typeof message === "object") {
        const firstKey = Object.keys(message)[0];
        const messages = message[firstKey];
        errorMessage = Array.isArray(messages)
          ? messages.join("; ")
          : messages;
      }
    }

    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: errorMessage,
    });
  } finally {
    setLoading(false);
  }
};

      // Fired when the user clicks "Proceed" after selecting a company.
      // Requests the OTP for the forgot-password flow, then moves the UI to the OTP step.
      const handleForgotPassword = async () => {
    if (!selectedCompanyId) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a company to proceed.',
      });
      return;
    }

    setLoading(true);
    try {
      // const token = localStorage.getItem("token");
      // const companyid = localStorage.getItem("companyId");
      // const memberid = localStorage.getItem("memberId");

      const res = await axios.post(
        `${BASE_URL}/member-auth/member_forget_password`,
        {
          email: email,
          company_id: selectedCompanyId,
        }
      );

    
      setStep(2);
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
        if (typeof message === 'string') errorMessage = message;
        else if (Array.isArray(message)) errorMessage = message.join('; ');
        else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          errorMessage = Array.isArray(messages) ? messages.join('; ') : messages;
        }
      }
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

      // NOTE: existing handler kept as-is but not currently wired to any button.
 const handleVerifyOtp = async () => {
  setLoading(true);

  try {
    const token = localStorage.getItem("token");

    await axios.post(
      `${BASE_URL}/member-auth/member_verify_forgetOtp`,
      {
        email: email,
        otp: otp.join(""),
        company_id: selectedCompanyId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    // OTP verified successfully
    setStep(3);

    Swal.fire({
      icon: "success",
      title: "Verified",
      text: "OTP verified successfully.",
      timer: 1500,
      showConfirmButton: false,
    });

  } catch (error) {
    let errorMessage = "An error occurred. Please try again.";

    if (error.response?.data?.message) {
      const message = error.response.data.message;

      if (typeof message === "string") {
        errorMessage = message;
      } else if (Array.isArray(message)) {
        errorMessage = message.join("; ");
      } else if (typeof message === "object") {
        const firstKey = Object.keys(message)[0];
        const messages = message[firstKey];
        errorMessage = Array.isArray(messages)
          ? messages.join("; ")
          : messages;
      }
    }

    Swal.fire({
      icon: "error",
      title: "Verification Failed",
      text: errorMessage,
    });
  } finally {
    setLoading(false);
  }
};


const handleResetPassword = async (e) => {
  e.preventDefault();

  if (!password || !password1) {
    Swal.fire({
      icon: "warning",
      title: "Missing Fields",
      text: "Please enter and confirm your new password.",
    });
    return;
  }

    if (password.length < 6 || password1.length < 6) {
      Swal.fire({
        icon: "warning",
        title: "Password Too Short",
        text: "New password must be at least 6 characters long.",
        confirmButtonColor: "#f59e0b",
      });
      return;
    }

  if (password !== password1) {
    Swal.fire({
      icon: "warning",
      title: "Password Mismatch",
      text: "Password and Confirm Password do not match.",
    });
    return;
  }

  setLoading(true);

  try {
    await axios.post(`${BASE_URL}/member-auth/member_reset_password`, {
      email,
      password,
      confirm_password: password1, 
      company_id: selectedCompanyId,
      // change this if your API expects a different key
    });

    Swal.fire({
      icon: "success",
      title: "Password Reset Successful",
      text: "Your password has been reset successfully.",
      timer: 1800,
      showConfirmButton: false,
    }).then(() => {
      navigate("/login");
    });

  } catch (error) {
    let errorMessage = "An error occurred. Please try again.";

    if (error.response?.data?.message) {
      const message = error.response.data.message;

      if (typeof message === "string") {
        errorMessage = message;
      } else if (Array.isArray(message)) {
        errorMessage = message.join("; ");
      } else if (typeof message === "object") {
        const firstKey = Object.keys(message)[0];
        const messages = message[firstKey];
        errorMessage = Array.isArray(messages)
          ? messages.join("; ")
          : messages;
      }
    }

    Swal.fire({
      icon: "error",
      title: "Reset Failed",
      text: errorMessage,
    });
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  if (step !== 2) return;

  // Reset timer whenever Step 2 is entered
  setTimer(165);

  const interval = setInterval(() => {
    setTimer((prev) => {
      if (prev <= 1) {
        clearInterval(interval);
        return 0;
      }

      return prev - 1;
    });
  }, 1000);

  return () => clearInterval(interval);
}, [step]);


// FEATURE UPDATE; 
{/* <button
  disabled={loading || timer === 0}
  type="submit"
  className={styles.submitActionBtn}
>
  <LockKeyhole color="white" />
  {loading
    ? "Verifying..."
    : timer === 0
    ? "OTP Expired"
    : "Verify OTP"}
</button>
{timer === 0 && (
  <button
    type="button"
    className={styles.resendBtn}
    onClick={handleForgotPassword}
  >
    Resend OTP
  </button>
)} */}

  return (
    <div className={styles.screenWrapper}>
      {companiesLoading && (
        <div className={styles.globalLoaderOverlay}>
          <div className={styles.spinnerContainer}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Fetching registered companies...</p>
          </div>
        </div>
      )}
      
      {/* LEFT COLUMN: BRAND SIDEBAR */}
      <div className={styles.leftSidebar}>
        <div className={styles.sidebarOverlay}></div>
        <div className={styles.sidebarContentWrapper}>
          <div className={styles.brandTopArea}>
            <div className={styles.logoContainer}>
              <img src={BRANDING?.coopPrimary?.logo} alt="RCCG Logo" className={styles.logoImg} />
            </div>
          </div>
          
          <div className={styles.sidebarMidContent}>
            <h1>Stronger Together,<br />Better Together</h1>
            <p>Empowering members to save, borrow, <br /> invest and grow financially.</p>
            <div className={styles.sideBarBottom} />
            <ul className={styles.featuresList}>
              <li>
                <span className={styles.featureIcon}><GoShieldCheck size={25} /></span>
                <div>
                  <strong>Secure & Trusted</strong>
                  <p>Your data is protected with enterprise-grade security</p>
                </div>
              </li>
              <li>
                <span className={styles.featureIcon}><RiPieChart2Fill size={25}/></span>
                <div>
                  <strong>Smart & Transparent</strong>
                  <p>Track your savings, loans and investments in real time</p>
                </div>
              </li>
              <li>
                <span className={styles.featureIcon}><HiMiniUsers size={25}/></span>
                <div>
                  <strong>Member Focused</strong>
                  <p>Built to support your financial wellbeing and future</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN: INTERACTIVE PORTAL - only shown on step 1 (email + company selection) */}
      {step === 1 && (
      <div className={styles.rightContentArea}>
        <div className={styles.portalActionCard}>
          
              <div className={styles.cardHeaderArea}>
                <h3>Forgot Password</h3>
                <p>Enter your registered email address</p>
              </div>

              <form 
              // onSubmit={handleLoginSubmit} 
              onSubmit={(e) => e.preventDefault()}
              className={styles.formSpace}>
                {/* Field 1: Email */}
                <div className={styles.cleanInputGroup}>
                  <label>Email Address</label>
                  <div className={styles.fieldInteractiveWrapper}>
                    <span className={styles.inputPrefixIcon}><FaRegUser size={18} color='#000'/></span>
                    <input 
                      type="text" 
                      required 
                      value={identifier}
                      onChange={handleIdentifierChange}
                      placeholder="Enter your email address" 
                      className={styles.cleanInput} 
                    />
                  </div>
                  {emailError && <span className={styles.errorText} style={{ color: 'red', fontSize: '12px' }}>{emailError}</span>}
                </div>

                {/* Field: Associated Company - only shown once companies have been fetched for the entered email */}
                {showCompanySelection && (
                <div className={styles.cleanInputGroup}>
                                  <label>Associated Company</label>
                                  <div className={styles.fieldInteractiveWrapper}>
                                    <span className={styles.inputPrefixIcon}><Building2 size={18} color='#000' /></span>
                                    <select
                                      required
                                      value={selectedCompanyId}
                                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                                      className={styles.cleanInput}
                                      style={{ appearance: 'auto', paddingLeft: '40px' }}
                                    >
                                      <option value="" disabled>-- Select your company --</option>
                                      {companies.map((workspace, index) => {
                                        const compId = workspace?.company?.id;
                                        const compName = workspace?.company?.name || "Unknown Company";
                                        return (
                                          <option key={compId || index} value={compId}>
                                            {compName}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>
                                </div>
                )}

                {/* Field 2: Password */}
                {/* <div className={styles.cleanInputGroup}>
                  <label>Password</label>
                  <div className={styles.fieldInteractiveWrapper}>
                    <span className={styles.inputPrefixIcon}><LuLockKeyhole size={18} color='#000'/></span>
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className={styles.cleanInput}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className={styles.endFieldIcon}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div> */}

                {/* Remember Me & Forgot Password */}
                {/* <div className={styles.formOptionsRow}> */}
                  {/* <label className={styles.rememberMeLabel}>
                    <input 
                      type="checkbox" 
                      checked={rememberMe} 
                      onChange={(e) => setRememberMe(e.target.checked)} 
                    />
                    <span>Remember me</span>
                  </label> */}
                  {/* <span className={styles.forgotPasswordLink}>Forgot Password?</span>
                </div> */}

                {/* Continue Button - shown by default, before companies have been fetched */}
                {!showCompanySelection && (
                  <button type="button" className={styles.submitActionBtn} disabled={loading} onClick={handleContinue}>
                    <span>{loading ? "Processing..." : "Continue"}</span>
                    {!loading && <ArrowRight size={18} />}
                  </button>
                )}

                {/* Proceed Button - shown once a company has been fetched/selected */}
                {showCompanySelection && (
                  <button type="button" className={styles.submitActionBtn} disabled={loading} onClick={handleForgotPassword}>
                    <span>{loading ? "Processing..." : "Proceed"}</span>
                    {!loading && <ArrowRight size={18} />}
                  </button>
                )}
              </form>

              {/* <div className={styles.supportCenterFooter}>
                Don't have portal access? <span style={{color: '#16A34A', cursor: 'pointer'}} onClick={() => navigate('/member-registration')}>Click here!</span>
              </div> */}
        
        </div>
      </div>
      )}

       {step === 2 && (
           <div className={styles.rightContentArea}>
                  <form 
                  // onSubmit={(e) => { 
                  //   e.preventDefault(); 
                  //   handle2ndStep(); 
                  //   }}
                     onSubmit={(e) => {
                      e.preventDefault();
                      handleVerifyOtp();
                      }}
                     className={styles.formSpace2}>
                    <div className={styles.cardHeaderArea1}>
                       
                        <div className={styles.goBackWrapper}>
                        <div
                          className={styles.goBack}
                          onClick={() => {
                            if (!loading) {
                              setStep(1);
                            }
                          }}
                        >
                          <ArrowLeft size={16} />
                          <span>Go back</span>
                        </div>
                      </div>

                      <div className={styles.lockIconCircle}><Smartphone size={24} /></div>
                      <h3>Verify your Identity</h3>
                      <p className={styles.stepSubheading}>We have sent a 6-digit OTP to your registered email address</p>
                    </div>
      
                    <div className={styles.emailPill}>
                      <div className={styles.pillIcon}><Mail size={12} /></div>
                      <span>{email.replace(/(.{3})(.*)(?=@)/, "$1***")}</span>
                    </div>
      
                    <div className={styles.otpGrid}>
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          type="text"
                          maxLength={1}
                          value={digit}
                          ref={(el) => (otpRefs.current[index] = el)}
                          onChange={(e) => handleOtpChange(e.target.value, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          className={`${styles.otpInput} ${digit ? styles.otpInputFilled : ''}`}
                        />
                      ))}
                    </div>
                    
                    <div className={styles.timerWrapper}>
                      Code expires in: <span className={styles.greenText}>{Math.floor(timer/60)}:{(timer%60).toString().padStart(2,'0')}</span>
                    </div>
      
                    <div className={styles.securityCard}>
                      <div className={styles.securityEmblem}>
                        <ShieldCheck className={styles.securityCardIcon} size={24} />
                      </div>
                      <div>
                        <h4 className={styles.securityTitle}>For your security</h4>
                        <p className={styles.securityDescription}>Do not share this code with anyone</p>
                      </div>
                    </div>
      
                    <button disabled={loading} type="submit" className={styles.submitActionBtn}>
                      <LockKeyhole color='white' style={{borderWidth: 0.5}}/> {loading ? 'Verifying...' : 'Verify OTP'}
                    </button>
                  </form>
                </div>
                )}
          
        {step === 3 && (
           <div className={styles.rightContentArea1}>

             <div className={styles.goBackWrapper1}>
                        <div
                          className={styles.goBack}
                          onClick={() => {
                            if (!loading) {
                              setStep(2);
                            }
                          }}
                        >
                          <ArrowLeft size={16} />
                          <span>Go back</span>
                        </div>
                      </div>

        <div className={styles.portalActionCard}>

             
          
              <div className={styles.cardHeaderArea}>


                <h3>Password Reset</h3>
                <p>Enter your password</p>
              </div>

              <form 
              // onSubmit={handleLoginSubmit} 
               onSubmit={handleResetPassword}
              className={styles.formSpace}>
              

                {/* Field 2: Password */}
                <div className={styles.cleanInputGroup}>
                  <label>Enter New Password</label>
                  <div className={styles.fieldInteractiveWrapper}>
                    <span className={styles.inputPrefixIcon}><LuLockKeyhole size={18} color='#000'/></span>
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      className={styles.cleanInput}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className={styles.endFieldIcon}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                 <div className={styles.cleanInputGroup}>
                  <label>Confirm Password</label>
                  <div className={styles.fieldInteractiveWrapper}>
                    <span className={styles.inputPrefixIcon}><LuLockKeyhole size={18} color='#000'/></span>
                    <input
                      type={showPassword1 ? "text" : "password"}
                      required
                      value={password1}
                      onChange={(e) => setPassword1(e.target.value)}
                      placeholder="Enter your password"
                      className={styles.cleanInput}
                    />
                    <button type="button" onClick={() => setShowPassword1(!showPassword1)} className={styles.endFieldIcon}>
                      {showPassword1 ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

               

                {/* Sign In Button */}
                <button type="submit" className={styles.submitActionBtn} disabled={loading}>
                  <span>{loading ? "Resetting..." : "Reset Passowrd"}</span>
                  {!loading && <ArrowRight size={18} />}
                </button>
              </form>

            
        
        </div>
      </div>
        )}
        
    </div>
  );
}
